import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AnnouncementPanel } from '@innovamat/glimmer-components';
import type { Classroom } from '../../../../modules/classroom';

import { ClassSelectCard } from '../../components/class-select-card';
import { useJoinToClassroom } from '../../join-to-classroom-context';
import {
  ClassroomsPerCourse,
  Container,
  CourseBlocks,
  CourseTitle,
} from '../../join-to-classroom.styled';
import { SelectStepSkeleton } from '../shared/select-classrooms-step.skeleton';

import { SelectClassroomsEmptyState } from './select-classrooms-empty-state';

export function FirstTimeSelectClassroomsStep(): JSX.Element {
  const { t } = useTranslation();
  const {
    isLoadingClassrooms,
    courses,
    getClassroomsByCourse,
    handleClick,
    getCardStatus,
    classroomsQueryError,
    selectedCourses,
    openEditModal,
  } = useJoinToClassroom();

  const coursesToRender =
    selectedCourses.length > 0
      ? courses.filter((course) => selectedCourses.includes(course.id))
      : undefined;
  const hasCoursesToRender = coursesToRender && coursesToRender.length > 0;

  return (
    <Container>
      {isLoadingClassrooms && <SelectStepSkeleton />}
      {classroomsQueryError && (
        <AnnouncementPanel
          text={t('classrooms.request.error')}
          type="error"
          canClose={false}
        />
      )}

      {hasCoursesToRender &&
        coursesToRender.map((course: any) => (
          <CourseBlocks key={course.id}>
            <CourseTitle>{course.name}</CourseTitle>
            <ClassroomsPerCourse>
              {getClassroomsByCourse(course.id).map((classroom) => (
                <ClassSelectCard
                  key={`card-${classroom.id}`}
                  classroom={classroom as Classroom}
                  onClick={() => handleClick(classroom)}
                  onClickEdit={() => openEditModal(classroom as Classroom)}
                  status={getCardStatus(classroom.id)}
                />
              ))}
            </ClassroomsPerCourse>
          </CourseBlocks>
        ))}

      {!hasCoursesToRender && <SelectClassroomsEmptyState />}
    </Container>
  );
}

export function SelectClassroomsStep(): JSX.Element {
  const { t } = useTranslation();
  const {
    isLoadingClassrooms,
    courses,
    getClassroomsByCourse,
    handleClick,
    getCardStatus,
    classroomsQueryError,
    selectedCourses,
    openEditModal,
  } = useJoinToClassroom();

  const coursesToRender =
    selectedCourses.length > 0
      ? courses.filter((course) => selectedCourses.includes(course.id))
      : courses;

  const hasCoursesToRender = coursesToRender && coursesToRender.length > 0;

  const [isFakeLoading, setIsFakeLoading] = useState(true);

  useEffect(() => {
    setTimeout(() => {
      setIsFakeLoading(false);
    }, 500);
  }, []);

  if (isLoadingClassrooms || isFakeLoading) {
    return (
      <Container>
        <SelectStepSkeleton />
      </Container>
    );
  }

  return (
    <Container>
      {classroomsQueryError && (
        <AnnouncementPanel
          text={t('classrooms.request.error')}
          type="error"
          canClose={false}
        />
      )}

      {hasCoursesToRender &&
        coursesToRender.map((course: any) => (
          <CourseBlocks key={course.id}>
            <CourseTitle>{course.name}</CourseTitle>
            <ClassroomsPerCourse>
              {getClassroomsByCourse(course.id).map((classroom) => (
                <ClassSelectCard
                  key={`card-${classroom.id}`}
                  classroom={classroom as Classroom}
                  onClick={() => handleClick(classroom)}
                  onClickEdit={() => openEditModal(classroom as Classroom)}
                  status={getCardStatus(classroom.id)}
                />
              ))}
            </ClassroomsPerCourse>
          </CourseBlocks>
        ))}
    </Container>
  );
}
